export type ModeratorRole = 'radio' | 'programa' | 'club' | 'evento';

export type ModAction = 'ocultar' | 'eliminar' | 'silenciar' | 'bloquear' | 'reportar' | 'destacar';

export type AuditActionType = ModAction;

export interface ModeratorProfile {
  id: string;
  name: string;
  username: string;
  photoUrl: string;
  role: ModeratorRole;
  roleLabel: string;
  assignedEntity: string;
  assignedEntityLink: string;
  sinceDate: string;
  level: number;
  totalActions: number;
  isOnline: boolean;
  isVerified: boolean;
  stats: {
    ocultados: number;
    eliminados: number;
    silenciados: number;
    bloqueados: number;
    reportados: number;
    destacados: number;
  };
  recentActions: ModeratorActionRecord[];
}

export interface ModeratorActionRecord {
  id: string;
  moderatorId: string;
  moderatorName: string;
  moderatorPhoto: string;
  action: ModAction;
  actionLabel: string;
  targetType: string;
  targetName: string;
  targetUsername: string;
  reason: string;
  timestamp: string;
  timestampRelative: string;
  context: string;
  isReversible: boolean;
}

export interface ModActionConfig {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
  severity: 'baja' | 'media' | 'alta';
}

export interface BannedUser {
  id: string;
  name: string;
  username: string;
  photoUrl: string;
  bannedBy: string;
  bannedByUsername: string;
  bannedDate: string;
  bannedDateRelative: string;
  reason: string;
  duration: string;
  expiresDate: string;
  isPermanent: boolean;
}

export interface ModReport {
  id: string;
  reportedBy: string;
  reportedByUsername: string;
  reportedTarget: string;
  reportedTargetUsername: string;
  targetType: string;
  reason: string;
  status: 'pendiente' | 'revisado' | 'resuelto' | 'rechazado';
  statusLabel: string;
  assignedModerator: string;
  assignedModeratorPhoto: string;
  resolution?: string;
  timestamp: string;
  timestampRelative: string;
}

export interface AuditLogEntry {
  id: string;
  moderatorId: string;
  moderatorName: string;
  moderatorPhoto: string;
  moderatorRole: ModeratorRole;
  action: ModAction;
  targetType: string;
  targetName: string;
  targetId: string;
  previousState: string;
  newState: string;
  reason: string;
  ipHash: string;
  timestamp: string;
  timestampRelative: string;
  reversible: boolean;
  reverted: boolean;
}

export interface ModeratorDashboardStats {
  totalModerators: number;
  moderatorsOnline: number;
  totalActions: number;
  actionsToday: number;
  actionsThisWeek: number;
  totalBlocked: number;
  activeBlocks: number;
  totalReportsReceived: number;
  reportsPending: number;
  reportsResolved: number;
  totalAuditEntries: number;
  avgResponseTime: string;
}

export const moderatorDashboardStats: ModeratorDashboardStats = {
  totalModerators: 1280,
  moderatorsOnline: 487,
  totalActions: 2847500,
  actionsToday: 12934,
  actionsThisWeek: 89120,
  totalBlocked: 47200,
  activeBlocks: 3820,
  totalReportsReceived: 156400,
  reportsPending: 234,
  reportsResolved: 155800,
  totalAuditEntries: 3200000,
  avgResponseTime: '4.2 min',
};

export const moderatorRoleLabels: Record<ModeratorRole, string> = {
  radio: 'Moderador de Radio',
  programa: 'Moderador de Programa',
  club: 'Moderador de Club',
  evento: 'Moderador de Evento',
};

export const moderatorRoleIcons: Record<ModeratorRole, string> = {
  radio: 'ri-broadcast-line',
  programa: 'ri-calendar-check-line',
  club: 'ri-group-line',
  evento: 'ri-calendar-event-line',
};

export const moderatorRoleColors: Record<ModeratorRole, { badge: string; ring: string; icon: string; bg: string }> = {
  radio: { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', ring: 'ring-emerald-400', icon: 'text-emerald-600', bg: 'bg-emerald-50' },
  programa: { badge: 'bg-sky-100 text-sky-700 border-sky-200', ring: 'ring-sky-400', icon: 'text-sky-600', bg: 'bg-sky-50' },
  club: { badge: 'bg-amber-100 text-amber-700 border-amber-200', ring: 'ring-amber-400', icon: 'text-amber-600', bg: 'bg-amber-50' },
  evento: { badge: 'bg-violet-100 text-violet-700 border-violet-200', ring: 'ring-violet-400', icon: 'text-violet-600', bg: 'bg-violet-50' },
};

export const modActionConfig: Record<ModAction, ModActionConfig> = {
  ocultar: { label: 'Ocultar', icon: 'ri-eye-off-line', color: 'text-slate-600', bgColor: 'bg-slate-50', description: 'Ocultar contenido sin eliminarlo definitivamente', severity: 'baja' },
  eliminar: { label: 'Eliminar', icon: 'ri-delete-bin-line', color: 'text-red-600', bgColor: 'bg-red-50', description: 'Eliminar contenido de forma permanente', severity: 'alta' },
  silenciar: { label: 'Silenciar', icon: 'ri-volume-mute-line', color: 'text-amber-600', bgColor: 'bg-amber-50', description: 'Silenciar usuario temporalmente en chats y cabinas', severity: 'media' },
  bloquear: { label: 'Bloquear', icon: 'ri-forbid-line', color: 'text-red-700', bgColor: 'bg-red-100', description: 'Bloquear acceso a un usuario de forma temporal o permanente', severity: 'alta' },
  reportar: { label: 'Reportar', icon: 'ri-alert-line', color: 'text-orange-600', bgColor: 'bg-orange-50', description: 'Reportar contenido o usuario a moderación superior', severity: 'media' },
  destacar: { label: 'Destacar', icon: 'ri-star-line', color: 'text-rose-600', bgColor: 'bg-rose-50', description: 'Destacar contenido como relevante o ejemplar para la comunidad', severity: 'baja' },
};

export const moderators: ModeratorProfile[] = [
  // ═══════ MODERADORES DE RADIO (8) ═══════
  {
    id: 'mod-radio-1',
    name: 'Camila Ortega',
    username: '@camila_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting%20approachable%20yet%20responsible%20demeanor%20editorial%20photography&width=200&height=200&seq=mod-camila&orientation=squarish',
    role: 'radio',
    roleLabel: 'Moderador de Radio',
    assignedEntity: 'La Kalle 96.3 FM',
    assignedEntityLink: '/radio/la-kalle',
    sinceDate: '2025-03-15',
    level: 34,
    totalActions: 28470,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 8920, eliminados: 7420, silenciados: 5140, bloqueados: 3890, reportados: 1210, destacados: 1890 },
    recentActions: [],
  },
  {
    id: 'mod-radio-2',
    name: 'Mateo Herrera',
    username: '@mateo_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait%20modern%20lighting%20trustworthy%20responsible%20demeanor%20editorial%20photography&width=200&height=200&seq=mod-mateo&orientation=squarish',
    role: 'radio',
    roleLabel: 'Moderador de Radio',
    assignedEntity: 'Onda Cero 104.5 FM',
    assignedEntityLink: '/radio/onda-cero',
    sinceDate: '2024-10-20',
    level: 28,
    totalActions: 19340,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 6100, eliminados: 5200, silenciados: 3800, bloqueados: 2640, reportados: 890, destacados: 710 },
    recentActions: [],
  },
  {
    id: 'mod-radio-3',
    name: 'Lucía Vargas',
    username: '@lucia_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20caring%20expression%20gentle%20but%20firm%20demeanor%20modern%20studio%20portrait%20warm%20soft%20lighting%20trustworthy%20professional%20editorial%20photography&width=200&height=200&seq=mod-lucia&orientation=squarish',
    role: 'radio',
    roleLabel: 'Moderador de Radio',
    assignedEntity: 'Radio Felicidad 88.9 FM',
    assignedEntityLink: '/radio/radio-felicidad',
    sinceDate: '2025-01-08',
    level: 22,
    totalActions: 14200,
    isOnline: false,
    isVerified: true,
    stats: { ocultados: 4800, eliminados: 3900, silenciados: 2700, bloqueados: 1500, reportados: 680, destacados: 620 },
    recentActions: [],
  },
  {
    id: 'mod-radio-4',
    name: 'Santiago Rojas',
    username: '@santiago_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting%20disciplined%20trustworthy%20editorial%20photography&width=200&height=200&seq=mod-santiago&orientation=squarish',
    role: 'radio',
    roleLabel: 'Moderador de Radio',
    assignedEntity: 'Rock & Pop 95.1 FM',
    assignedEntityLink: '/radio/rock-pop',
    sinceDate: '2024-06-12',
    level: 31,
    totalActions: 23180,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 7400, eliminados: 6100, silenciados: 4200, bloqueados: 3200, reportados: 1100, destacados: 1180 },
    recentActions: [],
  },
  {
    id: 'mod-radio-5',
    name: 'Valentina Suárez',
    username: '@valentina_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20mid%20twenties%20gentle%20smile%20alert%20eyes%20modern%20studio%20portrait%20clean%20background%20reliable%20trustworthy%20editorial%20photography&width=200&height=200&seq=mod-valentina&orientation=squarish',
    role: 'radio',
    roleLabel: 'Moderador de Radio',
    assignedEntity: 'Tropicalísima 102.7 FM',
    assignedEntityLink: '/radio/tropicalisima',
    sinceDate: '2025-05-20',
    level: 18,
    totalActions: 10300,
    isOnline: false,
    isVerified: true,
    stats: { ocultados: 3400, eliminados: 2800, silenciados: 1900, bloqueados: 1200, reportados: 540, destacados: 460 },
    recentActions: [],
  },
  {
    id: 'mod-radio-6',
    name: 'Andrés Felipe Castro',
    username: '@andres_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20professional%20expression%20tidy%20modern%20style%20studio%20portrait%20balanced%20lighting%20responsible%20mature%20editorial%20photography&width=200&height=200&seq=mod-andres&orientation=squarish',
    role: 'radio',
    roleLabel: 'Moderador de Radio',
    assignedEntity: 'La Mega 107.3 FM',
    assignedEntityLink: '/radio/la-mega',
    sinceDate: '2025-08-15',
    level: 15,
    totalActions: 8500,
    isOnline: true,
    isVerified: false,
    stats: { ocultados: 2800, eliminados: 2200, silenciados: 1500, bloqueados: 1000, reportados: 500, destacados: 500 },
    recentActions: [],
  },

  // ═══════ MODERADORES DE PROGRAMA (6) ═══════
  {
    id: 'mod-prog-1',
    name: 'Gabriela Méndez',
    username: '@gabriela_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20focused%20determined%20expression%20clean%20modern%20studio%20portrait%20strong%20yet%20approachable%20vibe%20professional%20editorial%20photography&width=200&height=200&seq=mod-gabriela&orientation=squarish',
    role: 'programa',
    roleLabel: 'Moderador de Programa',
    assignedEntity: 'El Mañanero - La Kalle',
    assignedEntityLink: '/program/el-mananero',
    sinceDate: '2025-02-10',
    level: 26,
    totalActions: 17600,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 5600, eliminados: 4500, silenciados: 3200, bloqueados: 2400, reportados: 950, destacados: 950 },
    recentActions: [],
  },
  {
    id: 'mod-prog-2',
    name: 'Diego Armando Paz',
    username: '@diegopaz_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20attentive%20expression%20modern%20clean%20look%20studio%20portrait%20balanced%20lighting%20responsible%20editorial%20photography&width=200&height=200&seq=mod-diego-paz&orientation=squarish',
    role: 'programa',
    roleLabel: 'Moderador de Programa',
    assignedEntity: 'Carrusel Deportivo - Onda Cero',
    assignedEntityLink: '/program/carrusel-deportivo',
    sinceDate: '2025-04-22',
    level: 21,
    totalActions: 13400,
    isOnline: false,
    isVerified: true,
    stats: { ocultados: 4300, eliminados: 3500, silenciados: 2400, bloqueados: 1800, reportados: 720, destacados: 680 },
    recentActions: [],
  },
  {
    id: 'mod-prog-3',
    name: 'Mariana López',
    username: '@mariana_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20warm%20attentive%20expression%20gentle%20authority%20modern%20studio%20portrait%20soft%20professional%20lighting%20trustworthy%20editorial%20photography&width=200&height=200&seq=mod-mariana&orientation=squarish',
    role: 'programa',
    roleLabel: 'Moderador de Programa',
    assignedEntity: 'Tardes con Valentina - Radio Felicidad',
    assignedEntityLink: '/program/tardes-con-valentina',
    sinceDate: '2025-06-18',
    level: 17,
    totalActions: 9600,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 3100, eliminados: 2500, silenciados: 1700, bloqueados: 1300, reportados: 510, destacados: 490 },
    recentActions: [],
  },
  {
    id: 'mod-prog-4',
    name: 'Julián Cárdenas',
    username: '@julian_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20composed%20vigilant%20expression%20modern%20portrait%20clean%20studio%20lighting%20responsible%20focused%20editorial%20photography&width=200&height=200&seq=mod-julian&orientation=squarish',
    role: 'programa',
    roleLabel: 'Moderador de Programa',
    assignedEntity: 'Noches de Rock - Rock & Pop',
    assignedEntityLink: '/program/noches-de-rock',
    sinceDate: '2024-09-05',
    level: 24,
    totalActions: 15800,
    isOnline: false,
    isVerified: true,
    stats: { ocultados: 5000, eliminados: 4100, silenciados: 2800, bloqueados: 2000, reportados: 820, destacados: 1080 },
    recentActions: [],
  },

  // ═══════ MODERADORES DE CLUB (6) ═══════
  {
    id: 'mod-club-1',
    name: 'Natalia Romero',
    username: '@natalia_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait%20vibrant%20yet%20professional%20lighting%20editorial%20photography&width=200&height=200&seq=mod-natalia&orientation=squarish',
    role: 'club',
    roleLabel: 'Moderador de Club',
    assignedEntity: 'Club Fanáticos La Kalle',
    assignedEntityLink: '/clubes',
    sinceDate: '2025-03-01',
    level: 29,
    totalActions: 21000,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 6800, eliminados: 5400, silenciados: 3700, bloqueados: 2700, reportados: 1100, destacados: 1300 },
    recentActions: [],
  },
  {
    id: 'mod-club-2',
    name: 'Alejandro Quintero',
    username: '@alejandro_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20welcoming%20friendly%20expression%20community%20leader%20vibe%20studio%20portrait%20warm%20approachable%20lighting%20editorial%20photography&width=200&height=200&seq=mod-alejandro&orientation=squarish',
    role: 'club',
    roleLabel: 'Moderador de Club',
    assignedEntity: 'Club Rockeros Unidos',
    assignedEntityLink: '/clubes',
    sinceDate: '2024-11-15',
    level: 20,
    totalActions: 12800,
    isOnline: false,
    isVerified: true,
    stats: { ocultados: 4100, eliminados: 3300, silenciados: 2300, bloqueados: 1600, reportados: 680, destacados: 820 },
    recentActions: [],
  },
  {
    id: 'mod-club-3',
    name: 'Isabella Cruz',
    username: '@isabella_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20mid%20twenties%20bright%20inclusive%20expression%20community%20builder%20modern%20studio%20portrait%20warm%20engaging%20lighting%20editorial%20photography&width=200&height=200&seq=mod-isabella&orientation=squarish',
    role: 'club',
    roleLabel: 'Moderador de Club',
    assignedEntity: 'Club Vallenato y Más',
    assignedEntityLink: '/clubes',
    sinceDate: '2025-02-28',
    level: 19,
    totalActions: 11800,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 3800, eliminados: 3100, silenciados: 2100, bloqueados: 1400, reportados: 620, destacados: 780 },
    recentActions: [],
  },

  // ═══════ MODERADORES DE EVENTO (5) ═══════
  {
    id: 'mod-ev-1',
    name: 'Ricardo El Guardián',
    username: '@ricardo_guardian',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20forties%20experienced%20commanding%20yet%20fair%20expression%20security%20professional%20style%20studio%20portrait%20authoritative%20warm%20editorial%20photography&width=200&height=200&seq=mod-ricardo&orientation=squarish',
    role: 'evento',
    roleLabel: 'Moderador de Evento',
    assignedEntity: 'RFW Fest 2026 Medellín',
    assignedEntityLink: '/eventos',
    sinceDate: '2024-01-10',
    level: 42,
    totalActions: 38100,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 12400, eliminados: 9800, silenciados: 6800, bloqueados: 5100, reportados: 2100, destacados: 1900 },
    recentActions: [],
  },
  {
    id: 'mod-ev-2',
    name: 'Paola Andrea Díaz',
    username: '@paola_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20mid%20twenties%20alert%20organized%20expression%20event%20coordinator%20style%20studio%20portrait%20clean%20modern%20lighting%20editorial%20photography&width=200&height=200&seq=mod-paola&orientation=squarish',
    role: 'evento',
    roleLabel: 'Moderador de Evento',
    assignedEntity: 'Premios RFW 2026',
    assignedEntityLink: '/eventos',
    sinceDate: '2025-01-25',
    level: 27,
    totalActions: 18500,
    isOnline: false,
    isVerified: true,
    stats: { ocultados: 5900, eliminados: 4800, silenciados: 3300, bloqueados: 2400, reportados: 1000, destacados: 1100 },
    recentActions: [],
  },
  {
    id: 'mod-ev-3',
    name: 'Fernando El Juez',
    username: '@fernando_juez',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20thirties%20impartial%20fair%20expression%20judge-like%20composure%20studio%20portrait%20balanced%20professional%20lighting%20editorial%20photography&width=200&height=200&seq=mod-fernando&orientation=squarish',
    role: 'evento',
    roleLabel: 'Moderador de Evento',
    assignedEntity: 'Copa RFW 2026 - Gran Final',
    assignedEntityLink: '/copas',
    sinceDate: '2025-07-12',
    level: 33,
    totalActions: 25200,
    isOnline: true,
    isVerified: true,
    stats: { ocultados: 8100, eliminados: 6500, silenciados: 4500, bloqueados: 3400, reportados: 1400, destacados: 1300 },
    recentActions: [],
  },
];

export const recentActions: ModeratorActionRecord[] = [
  {
    id: 'act-1',
    moderatorId: 'mod-radio-1',
    moderatorName: 'Camila Ortega',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting%20approachable%20yet%20responsible%20demeanor%20editorial%20photography&width=80&height=80&seq=mod-camila&orientation=squarish',
    action: 'eliminar',
    actionLabel: 'Eliminar',
    targetType: 'Comentario',
    targetName: 'Comentario #892347',
    targetUsername: '@troll_frecuencia',
    reason: 'Lenguaje ofensivo y ataques personales hacia otro oyente',
    timestamp: '2026-06-21T09:50:00Z',
    timestampRelative: 'Hace 2 min',
    context: 'Chat de cabina - La Kalle 96.3 FM · Programa El Mañanero',
    isReversible: false,
  },
  {
    id: 'act-2',
    moderatorId: 'mod-club-1',
    moderatorName: 'Natalia Romero',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait%20vibrant%20yet%20professional%20lighting%20editorial%20photography&width=80&height=80&seq=mod-natalia&orientation=squarish',
    action: 'destacar',
    actionLabel: 'Destacar',
    targetType: 'Post',
    targetName: 'Post #441290',
    targetUsername: '@fan_lakalle',
    reason: 'Contenido de altísima calidad: análisis detallado de la historia de La Kalle con entrevistas a ex locutores',
    timestamp: '2026-06-21T09:45:00Z',
    timestampRelative: 'Hace 7 min',
    context: 'Club Fanáticos La Kalle · Muro del Club',
    isReversible: true,
  },
  {
    id: 'act-3',
    moderatorId: 'mod-ev-1',
    moderatorName: 'Ricardo El Guardián',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20forties%20experienced%20commanding%20yet%20fair%20expression%20security%20professional%20style%20studio%20portrait%20authoritative%20warm%20editorial%20photography&width=80&height=80&seq=mod-ricardo&orientation=squarish',
    action: 'bloquear',
    actionLabel: 'Bloquear',
    targetType: 'Usuario',
    targetName: 'Usuario #739201',
    targetUsername: '@spammer_rfw2026',
    reason: 'Spam masivo en foro del RFW Fest: 47 mensajes repetidos en 3 minutos promocionando sitios externos',
    timestamp: '2026-06-21T09:30:00Z',
    timestampRelative: 'Hace 22 min',
    context: 'Evento RFW Fest 2026 Medellín · Foro de Asistentes',
    isReversible: true,
  },
  {
    id: 'act-4',
    moderatorId: 'mod-prog-1',
    moderatorName: 'Gabriela Méndez',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20focused%20determined%20expression%20clean%20modern%20studio%20portrait%20strong%20yet%20approachable%20vibe%20professional%20editorial%20photography&width=80&height=80&seq=mod-gabriela&orientation=squarish',
    action: 'silenciar',
    actionLabel: 'Silenciar',
    targetType: 'Usuario',
    targetName: 'Usuario #558203',
    targetUsername: '@bocina_999',
    reason: 'Micrófono abierto con ruido excesivo durante segmento de entrevistas en vivo',
    timestamp: '2026-06-21T09:15:00Z',
    timestampRelative: 'Hace 37 min',
    context: 'Programa El Mañanero · Cabina en Vivo',
    isReversible: true,
  },
  {
    id: 'act-5',
    moderatorId: 'mod-radio-2',
    moderatorName: 'Mateo Herrera',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait%20modern%20lighting%20trustworthy%20responsible%20demeanor%20editorial%20photography&width=80&height=80&seq=mod-mateo&orientation=squarish',
    action: 'ocultar',
    actionLabel: 'Ocultar',
    targetType: 'Comentario',
    targetName: 'Comentario #771034',
    targetUsername: '@polemico_fan',
    reason: 'Spoiler no marcado del resultado del partido en chat de programa no deportivo',
    timestamp: '2026-06-21T09:10:00Z',
    timestampRelative: 'Hace 42 min',
    context: 'Chat de cabina - Onda Cero 104.5 FM · Programa Matinal',
    isReversible: true,
  },
  {
    id: 'act-6',
    moderatorId: 'mod-radio-4',
    moderatorName: 'Santiago Rojas',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting%20disciplined%20trustworthy%20editorial%20photography&width=80&height=80&seq=mod-santiago&orientation=squarish',
    action: 'reportar',
    actionLabel: 'Reportar',
    targetType: 'Usuario',
    targetName: 'Usuario #228844',
    targetUsername: '@cuenta_falsa_rp',
    reason: 'Posible suplantación del locutor Ricky Navarro con fines de estafa a oyentes',
    timestamp: '2026-06-21T09:05:00Z',
    timestampRelative: 'Hace 47 min',
    context: 'Comunidad Rock & Pop 95.1 FM · Perfil de Usuario',
    isReversible: false,
  },
  {
    id: 'act-7',
    moderatorId: 'mod-club-3',
    moderatorName: 'Isabella Cruz',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latina%20woman%20mid%20twenties%20bright%20inclusive%20expression%20community%20builder%20modern%20studio%20portrait%20warm%20engaging%20lighting%20editorial%20photography&width=80&height=80&seq=mod-isabella&orientation=squarish',
    action: 'destacar',
    actionLabel: 'Destacar',
    targetType: 'Evento',
    targetName: 'Evento #112034',
    targetUsername: '@club_vallenato',
    reason: 'Iniciativa comunitaria ejemplar: recaudación de fondos para escuelas de música rural',
    timestamp: '2026-06-21T08:50:00Z',
    timestampRelative: 'Hace 1 hora',
    context: 'Club Vallenato y Más · Eventos del Club',
    isReversible: true,
  },
  {
    id: 'act-8',
    moderatorId: 'mod-ev-3',
    moderatorName: 'Fernando El Juez',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20thirties%20impartial%20fair%20expression%20judge-like%20composure%20studio%20portrait%20balanced%20professional%20lighting%20editorial%20photography&width=80&height=80&seq=mod-fernando&orientation=squarish',
    action: 'eliminar',
    actionLabel: 'Eliminar',
    targetType: 'Comentario',
    targetName: 'Comentario #663901',
    targetUsername: '@antifairplay',
    reason: 'Comentario denigrante hacia concursante de la Copa durante transmisión en vivo',
    timestamp: '2026-06-21T08:30:00Z',
    timestampRelative: 'Hace 1 hora',
    context: 'Copa RFW 2026 · Chat de la Final',
    isReversible: false,
  },
  {
    id: 'act-9',
    moderatorId: 'mod-prog-4',
    moderatorName: 'Julián Cárdenas',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20composed%20vigilant%20expression%20modern%20portrait%20clean%20studio%20lighting%20responsible%20focused%20editorial%20photography&width=80&height=80&seq=mod-julian&orientation=squarish',
    action: 'bloquear',
    actionLabel: 'Bloquear',
    targetType: 'Usuario',
    targetName: 'Usuario #904112',
    targetUsername: '@escandaloso_rock',
    reason: 'Bloqueo preventivo durante debate sobre bandas: historial de 3 silencios previos por agresividad verbal',
    timestamp: '2026-06-21T00:15:00Z',
    timestampRelative: 'Hace 9 horas',
    context: 'Programa Noches de Rock · Cabina en Vivo',
    isReversible: true,
  },
  {
    id: 'act-10',
    moderatorId: 'mod-radio-1',
    moderatorName: 'Camila Ortega',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting%20approachable%20yet%20responsible%20demeanor%20editorial%20photography&width=80&height=80&seq=mod-camila&orientation=squarish',
    action: 'ocultar',
    actionLabel: 'Ocultar',
    targetType: 'Post',
    targetName: 'Post #782145',
    targetUsername: '@autopromo_888',
    reason: 'Autopromoción no autorizada en el feed público de la radio',
    timestamp: '2026-06-20T23:45:00Z',
    timestampRelative: 'Hace 10 horas',
    context: 'La Kalle 96.3 FM · Feed Público',
    isReversible: true,
  },
  {
    id: 'act-11',
    moderatorId: 'mod-radio-5',
    moderatorName: 'Valentina Suárez',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20mid%20twenties%20gentle%20smile%20alert%20eyes%20modern%20studio%20portrait%20clean%20background%20reliable%20trustworthy%20editorial%20photography&width=80&height=80&seq=mod-valentina&orientation=squarish',
    action: 'silenciar',
    actionLabel: 'Silenciar',
    targetType: 'Usuario',
    targetName: 'Usuario #125488',
    targetUsername: '@cantante_aficionado',
    reason: 'Canto repetido no solicitado en cabina durante 3 segmentos consecutivos',
    timestamp: '2026-06-20T22:30:00Z',
    timestampRelative: 'Hace 11 horas',
    context: 'Tropicalísima 102.7 FM · Cabina Nocturna',
    isReversible: true,
  },
  {
    id: 'act-12',
    moderatorId: 'mod-ev-2',
    moderatorName: 'Paola Andrea Díaz',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20mid%20twenties%20alert%20organized%20expression%20event%20coordinator%20style%20studio%20portrait%20clean%20modern%20lighting%20editorial%20photography&width=80&height=80&seq=mod-paola&orientation=squarish',
    action: 'reportar',
    actionLabel: 'Reportar',
    targetType: 'Concurso',
    targetName: 'Concurso #441901',
    targetUsername: '@anon_concursante',
    reason: 'Sospecha de uso de múltiples cuentas para votar en concurso de los Premios RFW',
    timestamp: '2026-06-20T18:00:00Z',
    timestampRelative: 'Hace 16 horas',
    context: 'Premios RFW 2026 · Sistema de Votación',
    isReversible: false,
  },
];

export const bannedUsers: BannedUser[] = [
  {
    id: 'ban-1',
    name: 'Spammer RFW 2026',
    username: '@spammer_rfw2026',
    photoUrl: 'https://readdy.ai/api/search-image?query=Generic%20anonymous%20avatar%20dark%20silhouette%20simple%20circle%20icon%20neutral%20gray%20background%20placeholder%20no%20identity&width=200&height=200&seq=mod-ban-1&orientation=squarish',
    bannedBy: 'Ricardo El Guardián',
    bannedByUsername: '@ricardo_guardian',
    bannedDate: '2026-06-21',
    bannedDateRelative: 'Hace 22 min',
    reason: 'Spam masivo en foro del RFW Fest',
    duration: '30 días',
    expiresDate: '2026-07-21',
    isPermanent: false,
  },
  {
    id: 'ban-2',
    name: 'Escandaloso Rock',
    username: '@escandaloso_rock',
    photoUrl: 'https://readdy.ai/api/search-image?query=Dark%20generic%20anonymous%20user%20profile%20silhouette%20neutral%20circular%20avatar%20simple%20icon%20placeholder%20minimal%20design&width=200&height=200&seq=mod-ban-2&orientation=squarish',
    bannedBy: 'Julián Cárdenas',
    bannedByUsername: '@julian_mod',
    bannedDate: '2026-06-21',
    bannedDateRelative: 'Hace 9 horas',
    reason: 'Agresividad verbal reiterada en debate',
    duration: '7 días',
    expiresDate: '2026-06-28',
    isPermanent: false,
  },
  {
    id: 'ban-3',
    name: 'Suplantador Navarro',
    username: '@cuenta_falsa_rp',
    photoUrl: 'https://readdy.ai/api/search-image?query=Plain%20generic%20user%20avatar%20silhouette%20neutral%20gray%20circle%20minimal%20anonymous%20profile%20placeholder%20flat%20design&width=200&height=200&seq=mod-ban-3&orientation=squarish',
    bannedBy: 'Santiago Rojas',
    bannedByUsername: '@santiago_mod',
    bannedDate: '2026-06-21',
    bannedDateRelative: 'Hace 47 min',
    reason: 'Suplantación de identidad de locutor',
    duration: 'Permanente',
    expiresDate: 'Nunca',
    isPermanent: true,
  },
  {
    id: 'ban-4',
    name: 'Troll Anónimo 777',
    username: '@troll_anon_777',
    photoUrl: 'https://readdy.ai/api/search-image?query=Anonymous%20user%20avatar%20silhouette%20dark%20circle%20minimal%20placeholder%20generic%20profile%20icon%20neutral%20background&width=200&height=200&seq=mod-ban-4&orientation=squarish',
    bannedBy: 'Mateo Herrera',
    bannedByUsername: '@mateo_mod',
    bannedDate: '2026-06-20',
    bannedDateRelative: 'Ayer',
    reason: 'Acoso sistemático a locutora en mensajes privados y comentarios públicos',
    duration: 'Permanente',
    expiresDate: 'Nunca',
    isPermanent: true,
  },
  {
    id: 'ban-5',
    name: 'Link Peligroso 2026',
    username: '@link_peligroso',
    photoUrl: 'https://readdy.ai/api/search-image?query=Generic%20anonymous%20user%20silhouette%20circle%20icon%20simple%20neutral%20gray%20background%20placeholder%20avatar%20minimal&width=200&height=200&seq=mod-ban-5&orientation=squarish',
    bannedBy: 'Camila Ortega',
    bannedByUsername: '@camila_mod',
    bannedDate: '2026-06-19',
    bannedDateRelative: 'Hace 2 días',
    reason: 'Distribución de enlaces maliciosos en cabina',
    duration: 'Permanente',
    expiresDate: 'Nunca',
    isPermanent: true,
  },
  {
    id: 'ban-6',
    name: 'Micro Abierto 999',
    username: '@bocina_999',
    photoUrl: 'https://readdy.ai/api/search-image?query=Simple%20anonymous%20avatar%20circle%20dark%20silhouette%20generic%20placeholder%20profile%20icon%20neutral%20minimal%20flat%20design&width=200&height=200&seq=mod-ban-6&orientation=squarish',
    bannedBy: 'Gabriela Méndez',
    bannedByUsername: '@gabriela_mod',
    bannedDate: '2026-06-18',
    bannedDateRelative: 'Hace 3 días',
    reason: 'Ruido excesivo y sabotaje de transmisión en vivo',
    duration: '14 días',
    expiresDate: '2026-07-02',
    isPermanent: false,
  },
];

export const modReports: ModReport[] = [
  {
    id: 'report-1',
    reportedBy: 'Lucía Fernández',
    reportedByUsername: '@lucia_radiofan',
    reportedTarget: 'SpamLink 99',
    reportedTargetUsername: '@spamlink_99',
    targetType: 'Comentario en Cabina',
    reason: 'Envío repetido de enlaces sospechosos en el chat de Onda Nocturna',
    status: 'resuelto',
    statusLabel: 'Resuelto',
    assignedModerator: 'Mateo Herrera',
    assignedModeratorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait%20modern%20lighting%20trustworthy%20responsible%20demeanor%20editorial%20photography&width=80&height=80&seq=mod-mateo&orientation=squarish',
    resolution: 'Usuario silenciado permanentemente y mensajes eliminados',
    timestamp: '2026-06-20T22:00:00Z',
    timestampRelative: 'Ayer',
  },
  {
    id: 'report-2',
    reportedBy: 'Carlos Mendoza',
    reportedByUsername: '@carlos_fan',
    reportedTarget: 'Hater Anónimo',
    reportedTargetUsername: '@hater_anon',
    targetType: 'Mensaje Privado',
    reason: 'Mensajes ofensivos no solicitados hacia varios miembros del club',
    status: 'resuelto',
    statusLabel: 'Resuelto',
    assignedModerator: 'Natalia Romero',
    assignedModeratorPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait%20vibrant%20yet%20professional%20lighting%20editorial%20photography&width=80&height=80&seq=mod-natalia&orientation=squarish',
    resolution: 'Usuario bloqueado permanentemente tras verificar 5 reportes similares',
    timestamp: '2026-06-19T15:00:00Z',
    timestampRelative: 'Hace 2 días',
  },
  {
    id: 'report-3',
    reportedBy: 'Javier El Loco Ramírez',
    reportedByUsername: '@elloco_javier',
    reportedTarget: 'Cuenta Falsa RP',
    reportedTargetUsername: '@cuenta_falsa_rp',
    targetType: 'Perfil de Usuario',
    reason: 'Perfil sospechoso que se hace pasar por locutor Ricky Navarro y pide dinero a oyentes',
    status: 'revisado',
    statusLabel: 'Revisado',
    assignedModerator: 'Santiago Rojas',
    assignedModeratorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting%20disciplined%20trustworthy%20editorial%20photography&width=80&height=80&seq=mod-santiago&orientation=squarish',
    resolution: 'Reporte elevado a Super Admin para verificación de IP y posible acción legal',
    timestamp: '2026-06-21T08:00:00Z',
    timestampRelative: 'Hace 2 horas',
  },
  {
    id: 'report-4',
    reportedBy: 'Ana Paula Torres',
    reportedByUsername: '@anapaula_oye',
    reportedTarget: 'Contenido Inapropiado',
    reportedTargetUsername: '@contenido_feo',
    targetType: 'Post Público',
    reason: 'Publicación con contenido discriminatorio en el feed de la radio',
    status: 'pendiente',
    statusLabel: 'Pendiente',
    assignedModerator: 'Valentina Suárez',
    assignedModeratorPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20mid%20twenties%20gentle%20smile%20alert%20eyes%20modern%20studio%20portrait%20clean%20background%20reliable%20trustworthy%20editorial%20photography&width=80&height=80&seq=mod-valentina&orientation=squarish',
    timestamp: '2026-06-21T09:30:00Z',
    timestampRelative: 'Hace 22 min',
  },
  {
    id: 'report-5',
    reportedBy: 'Sofía Hernández',
    reportedByUsername: '@sofia_oyente',
    reportedTarget: 'Troll Noches de Rock',
    reportedTargetUsername: '@troll_rock_night',
    targetType: 'Comportamiento en Vivo',
    reason: 'Interrupciones constantes con insultos durante la tertulia de Noches de Rock',
    status: 'resuelto',
    statusLabel: 'Resuelto',
    assignedModerator: 'Julián Cárdenas',
    assignedModeratorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20composed%20vigilant%20expression%20modern%20portrait%20clean%20studio%20lighting%20responsible%20focused%20editorial%20photography&width=80&height=80&seq=mod-julian&orientation=squarish',
    resolution: 'Bloqueo de 30 días tras historial de 3 advertencias previas',
    timestamp: '2026-06-18T23:30:00Z',
    timestampRelative: 'Hace 3 días',
  },
  {
    id: 'report-6',
    reportedBy: 'Alejandro Ramírez',
    reportedByUsername: '@ale_locutor',
    reportedTarget: 'Anti Fair Play',
    reportedTargetUsername: '@antifairplay',
    targetType: 'Chat de Evento',
    reason: 'Comentarios denigrantes hacia concursante durante final de la Copa RFW',
    status: 'resuelto',
    statusLabel: 'Resuelto',
    assignedModerator: 'Fernando El Juez',
    assignedModeratorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20thirties%20impartial%20fair%20expression%20judge-like%20composure%20studio%20portrait%20balanced%20professional%20lighting%20editorial%20photography&width=80&height=80&seq=mod-fernando&orientation=squarish',
    resolution: 'Comentarios eliminados y usuario silenciado por 14 días',
    timestamp: '2026-06-21T08:20:00Z',
    timestampRelative: 'Hace 1 hora',
  },
  {
    id: 'report-7',
    reportedBy: 'Felipe El Podcaster',
    reportedByUsername: '@felipe_podcast',
    reportedTarget: 'Mensaje Anónimo',
    reportedTargetUsername: '@anon_sospechoso',
    targetType: 'Mensaje en Foro',
    reason: 'Mensaje con lenguaje que incita al odio en el foro del club de podcasters',
    status: 'rechazado',
    statusLabel: 'Rechazado',
    assignedModerator: 'Isabella Cruz',
    assignedModeratorPhoto: 'https://readdy.ai/api/search-image?query=Latina%20woman%20mid%20twenties%20bright%20inclusive%20expression%20community%20builder%20modern%20studio%20portrait%20warm%20engaging%20lighting%20editorial%20photography&width=80&height=80&seq=mod-isabella&orientation=squarish',
    resolution: 'Reporte rechazado: el mensaje es parte de un debate académico sobre límites del humor. No viola las normas.',
    timestamp: '2026-06-20T12:00:00Z',
    timestampRelative: 'Ayer',
  },
];

export const auditLog: AuditLogEntry[] = [
  {
    id: 'audit-1',
    moderatorId: 'mod-radio-1',
    moderatorName: 'Camila Ortega',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting%20approachable%20yet%20responsible%20demeanor%20editorial%20photography&width=80&height=80&seq=mod-camila&orientation=squarish',
    moderatorRole: 'radio',
    action: 'eliminar',
    targetType: 'Comentario',
    targetName: 'Comentario #892347',
    targetId: 'comment-892347',
    previousState: 'Visible · Contenido: "Este programa es una m..." (texto original ofensivo)',
    newState: 'Eliminado · No visible para ningún usuario',
    reason: 'Lenguaje ofensivo y ataques personales hacia otro oyente',
    ipHash: 'a1b2c3d4e5f6',
    timestamp: '2026-06-21T09:50:00Z',
    timestampRelative: 'Hace 2 min',
    reversible: false,
    reverted: false,
  },
  {
    id: 'audit-2',
    moderatorId: 'mod-ev-1',
    moderatorName: 'Ricardo El Guardián',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20forties%20experienced%20commanding%20yet%20fair%20expression%20security%20professional%20style%20studio%20portrait%20authoritative%20warm%20editorial%20photography&width=80&height=80&seq=mod-ricardo&orientation=squarish',
    moderatorRole: 'evento',
    action: 'bloquear',
    targetType: 'Usuario',
    targetName: 'Usuario #739201',
    targetId: 'user-739201',
    previousState: 'Activo · Último login: 2026-06-21 09:28',
    newState: 'Bloqueado · Temporal 30 días · Expira: 2026-07-21',
    reason: 'Spam masivo en foro del RFW Fest: 47 mensajes repetidos en 3 minutos',
    ipHash: 'b2c3d4e5f6a1',
    timestamp: '2026-06-21T09:30:00Z',
    timestampRelative: 'Hace 22 min',
    reversible: true,
    reverted: false,
  },
  {
    id: 'audit-3',
    moderatorId: 'mod-prog-1',
    moderatorName: 'Gabriela Méndez',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20focused%20determined%20expression%20clean%20modern%20studio%20portrait%20strong%20yet%20approachable%20vibe%20professional%20editorial%20photography&width=80&height=80&seq=mod-gabriela&orientation=squarish',
    moderatorRole: 'programa',
    action: 'silenciar',
    targetType: 'Usuario',
    targetName: 'Usuario #558203',
    targetId: 'user-558203',
    previousState: 'Activo · Micrófono abierto con ruido',
    newState: 'Silenciado · 24 horas · Expira: 2026-06-22 09:15',
    reason: 'Micrófono abierto con ruido excesivo durante segmento de entrevistas en vivo',
    ipHash: 'c3d4e5f6a1b2',
    timestamp: '2026-06-21T09:15:00Z',
    timestampRelative: 'Hace 37 min',
    reversible: true,
    reverted: false,
  },
  {
    id: 'audit-4',
    moderatorId: 'mod-radio-2',
    moderatorName: 'Mateo Herrera',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait%20modern%20lighting%20trustworthy%20responsible%20demeanor%20editorial%20photography&width=80&height=80&seq=mod-mateo&orientation=squarish',
    moderatorRole: 'radio',
    action: 'ocultar',
    targetType: 'Comentario',
    targetName: 'Comentario #771034',
    targetId: 'comment-771034',
    previousState: 'Visible · Contenía spoiler del partido',
    newState: 'Oculto · Solo visible para moderadores',
    reason: 'Spoiler no marcado del resultado del partido en chat de programa no deportivo',
    ipHash: 'd4e5f6a1b2c3',
    timestamp: '2026-06-21T09:10:00Z',
    timestampRelative: 'Hace 42 min',
    reversible: true,
    reverted: false,
  },
  {
    id: 'audit-5',
    moderatorId: 'mod-club-1',
    moderatorName: 'Natalia Romero',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait%20vibrant%20yet%20professional%20lighting%20editorial%20photography&width=80&height=80&seq=mod-natalia&orientation=squarish',
    moderatorRole: 'club',
    action: 'destacar',
    targetType: 'Post',
    targetName: 'Post #441290',
    targetId: 'post-441290',
    previousState: 'Normal · Visibilidad estándar',
    newState: 'Destacado · Prioridad alta en el feed del club',
    reason: 'Contenido de altísima calidad: análisis detallado de la historia de La Kalle',
    ipHash: 'e5f6a1b2c3d4',
    timestamp: '2026-06-21T09:45:00Z',
    timestampRelative: 'Hace 7 min',
    reversible: true,
    reverted: false,
  },
  {
    id: 'audit-6',
    moderatorId: 'mod-radio-4',
    moderatorName: 'Santiago Rojas',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting%20disciplined%20trustworthy%20editorial%20photography&width=80&height=80&seq=mod-santiago&orientation=squarish',
    moderatorRole: 'radio',
    action: 'reportar',
    targetType: 'Usuario',
    targetName: 'Usuario #228844',
    targetId: 'user-228844',
    previousState: 'Activo · Perfil con foto y nombre similar a locutor Ricky Navarro',
    newState: 'Reportado a Super Admin · En investigación',
    reason: 'Posible suplantación del locutor Ricky Navarro con fines de estafa a oyentes',
    ipHash: 'f6a1b2c3d4e5',
    timestamp: '2026-06-21T09:05:00Z',
    timestampRelative: 'Hace 47 min',
    reversible: false,
    reverted: false,
  },
  {
    id: 'audit-7',
    moderatorId: 'mod-ev-3',
    moderatorName: 'Fernando El Juez',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20thirties%20impartial%20fair%20expression%20judge-like%20composure%20studio%20portrait%20balanced%20professional%20lighting%20editorial%20photography&width=80&height=80&seq=mod-fernando&orientation=squarish',
    moderatorRole: 'evento',
    action: 'eliminar',
    targetType: 'Comentario',
    targetName: 'Comentario #663901',
    targetId: 'comment-663901',
    previousState: 'Visible · Comentario denigrante',
    newState: 'Eliminado · No recuperable',
    reason: 'Comentario denigrante hacia concursante de la Copa durante transmisión en vivo',
    ipHash: 'a1c3e5b2d4f6',
    timestamp: '2026-06-21T08:30:00Z',
    timestampRelative: 'Hace 1 hora',
    reversible: false,
    reverted: false,
  },
  {
    id: 'audit-8',
    moderatorId: 'mod-prog-4',
    moderatorName: 'Julián Cárdenas',
    moderatorPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20composed%20vigilant%20expression%20modern%20portrait%20clean%20studio%20lighting%20responsible%20focused%20editorial%20photography&width=80&height=80&seq=mod-julian&orientation=squarish',
    moderatorRole: 'programa',
    action: 'bloquear',
    targetType: 'Usuario',
    targetName: 'Usuario #904112',
    targetId: 'user-904112',
    previousState: 'Activo con advertencia · 3 silencios previos',
    newState: 'Bloqueado · Temporal 7 días · Expira: 2026-06-28',
    reason: 'Bloqueo tras 3 silencios previos por agresividad verbal en debate',
    ipHash: 'b2d4f6a1c3e5',
    timestamp: '2026-06-21T00:15:00Z',
    timestampRelative: 'Hace 9 horas',
    reversible: true,
    reverted: false,
  },
];

export const moderatorRules = [
  {
    id: 1,
    title: 'Todo debe quedar auditado',
    description: 'Cada acción de moderación — ocultar, eliminar, silenciar, bloquear, reportar, destacar — queda registrada en el sistema de auditoría con fecha, hora, moderador responsable, motivo y estado anterior y posterior. Nada se borra sin dejar huella. La transparencia es la base de la confianza comunitaria.',
    icon: 'ri-file-search-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
  },
  {
    id: 2,
    title: 'Proteger, no censurar',
    description: 'La moderación existe para mantener comunidades sanas, no para silenciar opiniones. Se actúa contra el spam, el odio, el acoso y la desinformación. Las diferencias de opinión son bienvenidas; los ataques personales no. Cada acción debe tener un motivo claro y proporcional.',
    icon: 'ri-shield-check-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
  {
    id: 3,
    title: 'Actuar con proporcionalidad',
    description: 'La severidad de la acción debe corresponder a la gravedad de la falta. Una primera ofensa menor recibe advertencia; la reincidencia escala a silencio; el comportamiento malicioso deliberado escala a bloqueo. El objetivo es educar y corregir, no castigar por castigar.',
    icon: 'ri-scales-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
];

export function getModeratorsByRole(role: ModeratorRole | 'todas'): ModeratorProfile[] {
  if (role === 'todas') return moderators;
  return moderators.filter((m) => m.role === role);
}

export function getOnlineModerators(): ModeratorProfile[] {
  return moderators.filter((m) => m.isOnline);
}

export function getRecentActionsByAction(action: ModAction | 'todas'): ModeratorActionRecord[] {
  if (action === 'todas') return recentActions;
  return recentActions.filter((a) => a.action === action);
}

export function getReportsByStatus(status: ModReport['status'] | 'todas'): ModReport[] {
  if (status === 'todas') return modReports;
  return modReports.filter((r) => r.status === status);
}

export function getAuditLogByAction(action: ModAction | 'todas'): AuditLogEntry[] {
  if (action === 'todas') return auditLog;
  return auditLog.filter((e) => e.action === action);
}

export function getActionStats(): { action: ModAction; count: number }[] {
  const total = { ocultar: 0, eliminar: 0, silenciar: 0, bloquear: 0, reportar: 0, destacar: 0 };
  moderators.forEach((m) => {
    total.ocultar += m.stats.ocultados;
    total.eliminar += m.stats.eliminados;
    total.silenciar += m.stats.silenciados;
    total.bloquear += m.stats.bloqueados;
    total.reportar += m.stats.reportados;
    total.destacar += m.stats.destacados;
  });
  return (Object.keys(total) as ModAction[]).map((key) => ({ action: key, count: total[key] }));
}